import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CanvasEditorViewportContext, type CanvasEditorViewport } from './CanvasEditorViewportContext';
import './canvasEditorStage.css';

interface Props {
  board: { width: number; height: number };
  children: React.ReactNode;
  /** Klik op de lege achtergrond (niet op een frame) -- gebruikt om de
   * selectie op te heffen. */
  onBackgroundPointerDown?: () => void;
}

const MIN_SCALE_FACTOR = 0.3;
const MAX_SCALE = 4;

/**
 * Statische contain-fit-weergave van het hele bord (geen boog-animatie zoals
 * de speler -- bewerken wil alles tegelijk zien), met eenvoudige, DIRECTE
 * scroll-zoom + sleep-om-te-pannen op de lege achtergrond (sectie 17
 * "zoom en pan"). Dit is de EDITOR, geen live-presentatiescherm, dus geen
 * "spreker raakt de weg kwijt"-risico zoals bij Stap 1's besluit #3 om vrije
 * navigatie uit de speler te weren.
 */
export default function CanvasEditorStage({ board, children, onBackgroundPointerDown }: Props) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<CanvasEditorViewport>({ scale: 1, tx: 0, ty: 0 });
  const dragRef = useRef<{ x: number; y: number; tx: number; ty: number; pointerId: number } | null>(null);

  const containFit = useCallback(() => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return;
    const scale = Math.min(rect.width / board.width, rect.height / board.height);
    setViewport({
      scale,
      tx: rect.width / 2 - scale * (board.width / 2),
      ty: rect.height / 2 - scale * (board.height / 2),
    });
  }, [board.width, board.height]);

  useEffect(() => {
    containFit();
    window.addEventListener('resize', containFit);
    return () => window.removeEventListener('resize', containFit);
  }, [containFit]);

  useEffect(() => {
    if (boardRef.current) {
      boardRef.current.style.transform = `translate(${viewport.tx}px, ${viewport.ty}px) scale(${viewport.scale})`;
    }
  }, [viewport]);

  // React's onWheel is een PASSIEVE listener (browser-optimalisatie voor
  // scrollperformance) -- event.preventDefault() daarin wordt genegeerd, dus
  // trackpad-pinch/ctrl+scroll zoomde de hele pagina i.p.v. alleen het canvas.
  // Vandaar een handmatige, niet-passieve native listener i.p.v. de JSX-prop.
  const handleWheel = useCallback((event: WheelEvent) => {
    event.preventDefault();
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = event.clientX - rect.left;
    const my = event.clientY - rect.top;
    setViewport((prev) => {
      const boardX = (mx - prev.tx) / prev.scale;
      const boardY = (my - prev.ty) / prev.scale;
      const zoomFactor = Math.exp(-event.deltaY * 0.0015);
      const minScale = Math.min(rect.width / board.width, rect.height / board.height) * MIN_SCALE_FACTOR;
      const newScale = Math.min(MAX_SCALE, Math.max(minScale, prev.scale * zoomFactor));
      return { scale: newScale, tx: mx - boardX * newScale, ty: my - boardY * newScale };
    });
  }, [board.width, board.height]);

  useEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;
    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => node.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handlePointerDown = (event: React.PointerEvent) => {
    // Alleen pannen als er echt op de lege achtergrond wordt geklikt, niet
    // wanneer dit event van een frame (CanvasFrameEditable) omhoog borrelt.
    if (event.target !== stageRef.current && event.target !== boardRef.current) return;
    dragRef.current = { x: event.clientX, y: event.clientY, tx: viewport.tx, ty: viewport.ty, pointerId: event.pointerId };
    onBackgroundPointerDown?.();
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = event.clientX - dragRef.current.x;
    const dy = event.clientY - dragRef.current.y;
    setViewport((prev) => ({ ...prev, tx: dragRef.current!.tx + dx, ty: dragRef.current!.ty + dy }));
  };

  const endPan = () => { dragRef.current = null; };

  return (
    <CanvasEditorViewportContext.Provider value={viewport}>
      <div
        ref={stageRef}
        className="canvas-editor-stage"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endPan}
        onPointerCancel={endPan}
      >
        <div ref={boardRef} className="canvas-board canvas-editor-board" style={{ width: board.width, height: board.height }}>
          {children}
        </div>
      </div>
    </CanvasEditorViewportContext.Provider>
  );
}
